import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Ban, CircleAlert, Loader2, Mic, MicOff, RefreshCw } from 'lucide-react';
import { useAudioDevice } from 'providers/AudioDeviceProvider';
import { useAudioDevicePreview } from 'hooks/useAudioDevicePreview';
import type { AudioDeviceState, AudioOutputDevice } from 'utils/audio/audioDeviceTypes';
import { displayDeviceLabel } from 'utils/audio/displayDeviceLabel';
import { DeviceBadge, type DeviceBadgeTone } from './DeviceBadge';
import { deviceNames } from './deviceNames';
import { SupportHint } from './SupportHint';
import { TroubleshootingSteps } from './TroubleshootingSteps';
import { blockKeyForState, troubleshootingBlock } from './troubleshootingBlocks';
import {
  DeviceCard,
  DeviceCardActions,
  DeviceCardBadges,
  DeviceCardDescription,
  DeviceCardDivider,
  DeviceCardHeader,
  DeviceCardHeading,
  DeviceCardIcon,
  DeviceCardMaskedDescription,
  DeviceCardSearching,
  DeviceCardSummary,
  DeviceCardTitle,
  type IconTone,
  OpenSettingsButton,
  PreviewBox,
  PreviewLabel,
  PreviewLevelMeter,
  PreviewRow,
  PreviewSpeaker,
  PreviewSpeakerLabel,
  PreviewSpeakerName,
  PreviewTestTone,
  PreviewTestToneButton,
  RetestButton,
  UseDeviceButton,
} from './RecommendedDeviceCard.styles';

type Presentation = {
  Icon: typeof Mic;
  iconTone: IconTone;
  heading: string;
  /** Empty badges also drop the card's subtitle. */
  badges: { label: string; tone: DeviceBadgeTone }[];
  description?: string;
  extraDescription?: string;
  showTroubleshooting: boolean;
  showUseDevice: boolean;
};

const ConnectedPreview = ({
  deviceId,
  speaker,
}: {
  deviceId: string;
  speaker: AudioOutputDevice | null;
}) => {
  const level = useAudioDevicePreview(deviceId);
  const { t } = useTranslation();
  return (
    <PreviewBox>
      <PreviewRow>
        <PreviewLabel>{t('audio.settings.microphone')}</PreviewLabel>
        <PreviewLevelMeter level={level} label={t('audio.settings.levelRecommended')} bars={56} />
      </PreviewRow>
      {speaker && (
        <PreviewRow>
          <PreviewSpeaker>
            <PreviewSpeakerLabel>{t('audio.settings.speaker')}</PreviewSpeakerLabel>
            <PreviewSpeakerName>{displayDeviceLabel(speaker.label)}</PreviewSpeakerName>
          </PreviewSpeaker>
          <PreviewTestTone>
            <PreviewTestToneButton
              sinkId={speaker.deviceId}
              source="card"
              name={t('audio.settings.playTestToneCurrent')}
            />
          </PreviewTestTone>
        </PreviewRow>
      )}
    </PreviewBox>
  );
};

export const RecommendedDeviceCard = () => {
  const { t } = useTranslation();
  const audio = useAudioDevice();
  const [retesting, setRetesting] = useState(false);
  const retestSettledRef = useRef(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (retesting || !retestSettledRef.current) return;
    retestSettledRef.current = false;
    if (document.activeElement === document.body) sectionRef.current?.focus();
  }, [retesting]);

  if (audio.mode !== 'active' || audio.state === 'passive') return null;

  const retest = async () => {
    if (retesting) return;
    setRetesting(true);
    try {
      await audio.reevaluate();
      toast.success(t('audio.settings.retested'));
    } finally {
      retestSettledRef.current = true;
      setRetesting(false);
    }
  };

  const openWindowsSettings = async () => {
    try {
      await audio.openPrivacySettings();
    } catch {
      toast.error(t('audio.settings.openWindowsSettingsFailed'));
    }
  };

  const switchToRecommended = async () => {
    try {
      await audio.switchToRecommendedDevice();
    } catch {
      toast.error(t('audio.settings.saveFailed'));
    } finally {
      sectionRef.current?.focus();
    }
  };

  const state: AudioDeviceState = audio.state;
  const names = deviceNames(audio);
  const cardLabel = t('audio.settings.recommendedDevice');
  const recommended = {
    label: t('audio.settings.badges.recommended'),
    tone: 'recommended' as const,
  };
  const unavailable = { label: t('audio.settings.badges.unavailable'), tone: 'warning' as const };

  const jabraMissing = (extraDescription?: string): Presentation =>
    audio.settings.recommendedMicrophone
      ? {
          Icon: CircleAlert,
          iconTone: 'warning',
          heading: t('audio.settings.headings.missing', names),
          badges: [recommended, unavailable],
          description: extraDescription ? undefined : t('audio.settings.descriptions.missing'),
          extraDescription,
          showTroubleshooting: true,
          showUseDevice: false,
        }
      : {
          Icon: CircleAlert,
          iconTone: 'warning',
          heading: t('audio.settings.headings.neverSeen'),
          badges: [recommended, unavailable],
          description: t('audio.settings.descriptions.neverSeen'),
          extraDescription,
          showTroubleshooting: true,
          showUseDevice: false,
        };

  const jabraNextToConfirmed = (extraDescription?: string): Presentation =>
    audio.recommendedCandidate
      ? {
          Icon: Mic,
          iconTone: 'available',
          heading: t('audio.settings.headings.available', names),
          badges: [recommended, { label: t('audio.settings.badges.notConnected'), tone: 'muted' }],
          extraDescription,
          showTroubleshooting: false,
          showUseDevice: true,
        }
      : jabraMissing(extraDescription);

  const presentation = ((): Presentation | null => {
    switch (state) {
      case 'searching':
        return null;
      case 'recommendedConnected':
        return {
          Icon: Mic,
          iconTone: 'connected',
          heading: t('audio.settings.headings.connected', names),
          badges: [recommended, { label: t('audio.settings.badges.connected'), tone: 'success' }],
          showTroubleshooting: false,
          showUseDevice: false,
        };
      case 'confirmedConnected':
        return jabraNextToConfirmed();
      case 'recommendedMissing':
      case 'recommendedNeverSeen':
        return jabraMissing();
      case 'confirmedMissing':
        return jabraNextToConfirmed(t('audio.settings.descriptions.confirmedMissing', names));
      case 'confirmedInaccessible':
        return jabraNextToConfirmed(t('audio.settings.descriptions.confirmedInaccessible', names));
      case 'recommendedInaccessible':
        return {
          Icon: Ban,
          iconTone: 'warning',
          heading: t('audio.settings.headings.inaccessible'),
          badges: [recommended],
          description: t('audio.settings.descriptions.inaccessible'),
          showTroubleshooting: true,
          showUseDevice: false,
        };
      case 'accessBlocked':
        return {
          Icon: Ban,
          iconTone: 'error',
          heading: t('audio.settings.headings.accessBlocked'),
          badges: [],
          description: t('audio.settings.descriptions.accessBlocked'),
          showTroubleshooting: true,
          showUseDevice: false,
        };
      case 'noDevice':
        return {
          Icon: MicOff,
          iconTone: 'error',
          heading: t('audio.settings.headings.noDevice'),
          badges: [],
          description: t('audio.settings.descriptions.noDevice'),
          showTroubleshooting: true,
          showUseDevice: false,
        };
    }
  })();

  if (!presentation) {
    return (
      <DeviceCard aria-label={cardLabel}>
        <DeviceCardSearching>
          <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden />
          {t('audio.messages.searching')}
        </DeviceCardSearching>
        <DeviceCardDivider />
        <SupportHint />
      </DeviceCard>
    );
  }

  const blockKey = presentation.showTroubleshooting
    ? (blockKeyForState(state) ?? 'jabraMissing')
    : null;
  const { Icon } = presentation;

  return (
    <DeviceCard ref={sectionRef} tabIndex={-1} aria-label={cardLabel}>
      {presentation.badges.length > 0 && <DeviceCardTitle>{cardLabel}</DeviceCardTitle>}
      <DeviceCardHeader>
        <DeviceCardIcon $tone={presentation.iconTone}>
          <Icon className="size-4" aria-hidden />
        </DeviceCardIcon>
        <DeviceCardSummary>
          {/* Live region: screen readers hear the new verdict after a retest. */}
          <DeviceCardHeading role="status">{presentation.heading}</DeviceCardHeading>
          {presentation.badges.length > 0 && (
            <DeviceCardBadges>
              {presentation.badges.map((badge) => (
                <DeviceBadge key={badge.label} tone={badge.tone}>
                  {badge.label}
                </DeviceBadge>
              ))}
            </DeviceCardBadges>
          )}
          {presentation.description && (
            <DeviceCardDescription>{presentation.description}</DeviceCardDescription>
          )}
          {presentation.extraDescription && (
            <DeviceCardMaskedDescription>
              {presentation.extraDescription}
            </DeviceCardMaskedDescription>
          )}
        </DeviceCardSummary>
        {presentation.showUseDevice && (
          <UseDeviceButton onClick={() => void switchToRecommended()}>
            {t('audio.settings.useDevice')}
          </UseDeviceButton>
        )}
      </DeviceCardHeader>

      {state === 'recommendedConnected' && audio.selectedDevice && (
        <ConnectedPreview
          deviceId={audio.selectedDevice.deviceId}
          speaker={audio.selectedSpeaker}
        />
      )}

      {blockKey && <TroubleshootingSteps block={troubleshootingBlock(blockKey, t)} />}

      {presentation.showTroubleshooting && (
        <DeviceCardActions>
          <RetestButton aria-busy={retesting} onClick={() => void retest()}>
            <RefreshCw
              className={
                retesting ? 'size-3.5 animate-spin motion-reduce:animate-none' : 'size-3.5'
              }
              aria-hidden
            />
            {t('audio.settings.retest')}
          </RetestButton>
          {state === 'accessBlocked' && (
            <OpenSettingsButton onClick={() => void openWindowsSettings()}>
              {t('audio.settings.openWindowsSettings')}
            </OpenSettingsButton>
          )}
        </DeviceCardActions>
      )}

      <DeviceCardDivider />
      <SupportHint />
    </DeviceCard>
  );
};
